import React, { useState, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Sparkles, Search, FileText, Upload, Send, Loader2, ShieldCheck,
  ShieldAlert, FileSearch, RefreshCw, User, Lock, Bell
} from 'lucide-react';

// Lightweight markdown-ish renderer (bold, headings, bullets)
function RichText({ text }) {
  if (!text) return null;
  const renderInline = (str) =>
    str.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
      p.startsWith('**') && p.endsWith('**')
        ? <strong key={i} className="font-semibold text-slate-900 dark:text-slate-100">{p.slice(2, -2)}</strong>
        : <span key={i}>{p}</span>
    );
  return (
    <div className="space-y-1.5 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
      {text.split('\n').map((line, idx) => {
        const t = line.trim();
        if (!t) return <div key={idx} className="h-1" />;
        if (t.startsWith('- ') || t.startsWith('* '))
          return <div key={idx} className="flex gap-2 pl-1"><span className="text-sky-500 mt-0.5">•</span><span>{renderInline(t.slice(2))}</span></div>;
        if (/^#{1,6}\s/.test(t))
          return <h4 key={idx} className="font-bold text-slate-900 dark:text-slate-100 mt-2">{renderInline(t.replace(/^#{1,6}\s/, ''))}</h4>;
        return <p key={idx}>{renderInline(t)}</p>;
      })}
    </div>
  );
}

export default function DoctorRecordsAI() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [records, setRecords] = useState([]);
  const [consent, setConsent] = useState(null);

  const [summary, setSummary] = useState('');
  const [summaryMeta, setSummaryMeta] = useState(null);
  const [summarizing, setSummarizing] = useState(false);

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [answerMeta, setAnswerMeta] = useState(null);
  const [asking, setAsking] = useState(false);

  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadText, setUploadText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const requestConsent = async () => {
    if (!patient) return;
    setRequesting(true);
    try {
      const res = await api.post('/ai/records/request-consent', { patient_id: patient.patient_id });
      toast.success(res.data.note || 'Consent request sent to the patient.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the consent request.');
    } finally {
      setRequesting(false);
    }
  };

  const searchPatients = async (e) => {
    e?.preventDefault();
    try {
      const res = await api.get(`/ai/records/patients/search`, { params: { q: query } });
      setResults(res.data.patients || []);
      if ((res.data.patients || []).length === 0) toast.info('No patients found.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed.');
    }
  };

  const loadPatient = useCallback(async (p) => {
    setPatient(p);
    setResults([]);
    setSummary(''); setAnswer(''); setQuestion('');
    try {
      const res = await api.get(`/ai/records/${p.patient_id}`);
      setRecords(res.data.records || []);
      setConsent(res.data.consent || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load records.');
    }
  }, []);

  const doSummarize = async () => {
    if (!patient) return;
    setSummarizing(true); setSummary('');
    try {
      const res = await api.post(`/ai/records/summarize`, { patient_id: patient.patient_id });
      setSummary(res.data.summary);
      setSummaryMeta({ sources: res.data.sources, model: res.data.model, provider: res.data.provider, disclaimer: res.data.disclaimer });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Summarize failed.');
    } finally {
      setSummarizing(false);
    }
  };

  const doAsk = async (e) => {
    e?.preventDefault();
    if (!patient || !question.trim()) return;
    setAsking(true); setAnswer('');
    try {
      const res = await api.post(`/ai/records/ask`, { patient_id: patient.patient_id, question: question.trim() });
      setAnswer(res.data.answer);
      setAnswerMeta({ sources: res.data.sources, model: res.data.model, provider: res.data.provider });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Query failed.');
    } finally {
      setAsking(false);
    }
  };

  const doUpload = async () => {
    if (!patient || (!uploadText.trim())) { toast.info('Paste record text to upload.'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('patient_id', patient.patient_id);
      form.append('title', uploadTitle || 'Medical Record');
      form.append('text', uploadText);
      await api.post(`/ai/records/upload`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Record indexed.');
      setUploadText(''); setUploadTitle('');
      loadPatient(patient);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">AI Medical-Record Assistant</h1>
          <p className="text-xs text-slate-500">Consent-gated summaries &amp; Q&amp;A over a patient&apos;s uploaded records — decision support only.</p>
        </div>
      </div>

      {/* Patient search */}
      <Card className="mt-4">
        <CardContent className="p-4">
          <form onSubmit={searchPatients} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search patient by name or email…"
                className="w-full text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500" />
            </div>
            <Button type="submit" className="bg-sky-600 hover:bg-sky-700 text-white">Search</Button>
          </form>
          {results.length > 0 && (
            <div className="mt-3 grid gap-2">
              {results.map((p) => (
                <button key={p.patient_id} onClick={() => loadPatient(p)}
                  className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-sky-50 dark:hover:bg-sky-900/20 text-left">
                  <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center text-sky-700 dark:text-sky-300 font-semibold text-sm">
                    {(p.name || 'P')[0].toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.email} · {p.country}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {patient && (
        <>
          {/* Consent enforcement banner */}
          {consent && !consent.granted && (
            <div className="mt-4 rounded-xl border-2 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 flex items-start gap-3" data-testid="consent-locked-banner">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center shrink-0">
                <Lock className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-red-700 dark:text-red-300">AI features locked — patient consent required</div>
                <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">
                  This patient has not granted consent for AI to process their medical records. Summaries and Q&amp;A are disabled.
                  Ask the patient (or their case manager) to grant AI consent from the patient portal.
                </p>
                <Button onClick={requestConsent} disabled={requesting}
                  className="mt-2 h-8 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid="request-consent-btn">
                  {requesting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Bell className="w-3.5 h-3.5 mr-1.5" />}
                  Request consent from patient
                </Button>
              </div>
            </div>
          )}
          {/* Patient header + consent */}
          <Card className="mt-4">
            <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center text-white font-bold">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900 dark:text-white">{patient.name}</div>
                  <div className="text-xs text-slate-500">{patient.email} · {records.length} record(s) indexed</div>
                </div>
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${consent?.granted
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'}`}
                title="Consent is granted by the patient or case manager.">
                {consent?.granted ? <ShieldCheck className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
                {consent?.granted ? 'AI consent granted' : 'No AI consent — ask patient / case manager to grant'}
              </div>
            </CardContent>
          </Card>

          {/* Records list */}
          <Card className="mt-4">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-sky-600" /> Indexed Records
              </h3>
              {records.length === 0 ? (
                <p className="text-xs text-slate-500">No records yet. Paste one below to index it.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {records.map((r) => (
                    <Badge key={r.record_id} variant="outline" className="text-xs">
                      {r.title} · {r.char_count} chars
                    </Badge>
                  ))}
                </div>
              )}
              {/* Upload text */}
              <div className="mt-3 space-y-2 border-t border-slate-100 dark:border-slate-800 pt-3">
                <input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="Record title (e.g. Discharge Summary)"
                  className="w-full text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2" />
                <textarea value={uploadText} onChange={(e) => setUploadText(e.target.value)} rows={3}
                  placeholder="Paste medical record text to index…"
                  className="w-full text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2" />
                <Button onClick={doUpload} disabled={uploading} variant="outline" className="text-sm">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Index record
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Summarize */}
          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <FileSearch className="w-4 h-4 text-sky-600" /> Pre-consultation Summary
                </h3>
                <Button onClick={doSummarize} disabled={summarizing || !consent?.granted} className="bg-sky-600 hover:bg-sky-700 text-white text-sm h-8">
                  {summarizing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : !consent?.granted ? <Lock className="w-4 h-4 mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {summary ? 'Regenerate' : 'Generate summary'}
                </Button>
              </div>
              <div className="rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-4 min-h-[80px]">
                {summarizing ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Summarizing records…</div>
                ) : summary ? <RichText text={summary} /> : <p className="text-xs text-slate-400">Click Generate summary to build a cited, structured pre-consult summary.</p>}
              </div>
              {summaryMeta?.sources && (
                <p className="text-[11px] text-slate-500 mt-2">Sources: {summaryMeta.sources.join(', ')} · {summaryMeta.provider}/{summaryMeta.model}</p>
              )}
            </CardContent>
          </Card>

          {/* Ask */}
          <Card className="mt-4 mb-8">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                <Send className="w-4 h-4 text-sky-600" /> Ask the records
              </h3>
              <form onSubmit={doAsk} className="flex gap-2">
                <input value={question} onChange={(e) => setQuestion(e.target.value)}
                  placeholder="e.g. What is the ejection fraction? Any documented allergies?"
                  className="flex-1 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500" />
                <Button type="submit" disabled={asking || !question.trim() || !consent?.granted} className="bg-sky-600 hover:bg-sky-700 text-white">
                  {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : !consent?.granted ? <Lock className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
              {answer && (
                <div className="mt-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-4">
                  <RichText text={answer} />
                  {answerMeta?.sources && <p className="text-[11px] text-slate-500 mt-2">Sources: {answerMeta.sources.join(', ')}</p>}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Compliance footer */}
          <div className="flex items-start gap-2 rounded-lg bg-sky-50 dark:bg-sky-900/10 border border-sky-200 dark:border-sky-800 p-3 mb-8">
            <ShieldCheck className="w-4 h-4 text-sky-600 mt-0.5 shrink-0" />
            <p className="text-[11px] text-sky-800 dark:text-sky-300">
              {summaryMeta?.disclaimer || 'AI-generated clinical documentation support based only on uploaded records. Not a diagnosis. The treating doctor is responsible for all clinical decisions.'}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
